import type { CgtFormType } from '@/types/cgt'

// ─── Block color palette ───────────────────────────────────────────────────────
//
// One restrained, categorical color per CGT form type. `soft` styles the chip
// surface, `bar` the solid accent rail, `dot` the legend swatch. `hex`/`container`
// feed Schedule-X's `calendars` map (which needs concrete colors, not classes).

export interface BlockColor {
  /** Tailwind classes for the chip surface (bg + border + text). */
  soft: string
  /** Solid accent rail class. */
  bar: string
  /** Legend / status dot class. */
  dot: string
  /** Solid accent color for Schedule-X events. */
  hex: string
  container: string
  onContainer: string
}

const FALLBACK: BlockColor = {
  soft: 'bg-slate-50 border-slate-200 text-slate-700',
  bar: 'bg-slate-400',
  dot: 'bg-slate-400',
  hex: '#64748b',
  container: '#e2e8f0',
  onContainer: '#1e293b',
}

export const BLOCK_COLORS: Partial<Record<CgtFormType, BlockColor>> = {
  collection: {
    soft: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    bar: 'bg-emerald-500',
    dot: 'bg-emerald-500',
    hex: '#059669',
    container: '#d1fae5',
    onContainer: '#064e3b',
  },
  processing: {
    soft: 'bg-sky-50 border-sky-200 text-sky-900',
    bar: 'bg-sky-500',
    dot: 'bg-sky-500',
    hex: '#0284c7',
    container: '#e0f2fe',
    onContainer: '#075985',
  },
  product_release_review: {
    soft: 'bg-amber-50 border-amber-200 text-amber-900',
    bar: 'bg-amber-500',
    dot: 'bg-amber-500',
    hex: '#d97706',
    container: '#fef3c7',
    onContainer: '#92400e',
  },
  distribution: {
    soft: 'bg-violet-50 border-violet-200 text-violet-900',
    bar: 'bg-violet-500',
    dot: 'bg-violet-500',
    hex: '#7c3aed',
    container: '#ede9fe',
    onContainer: '#5b21b6',
  },
}

export function blockColor(formType?: CgtFormType): BlockColor {
  return (formType && BLOCK_COLORS[formType]) || FALLBACK
}

/** Legend rows for the form types we color, in workflow order. */
export const LEGEND: { formType: CgtFormType; label: string }[] = [
  { formType: 'collection', label: 'Collection' },
  { formType: 'processing', label: 'Processing' },
  { formType: 'product_release_review', label: 'Release Review' },
  { formType: 'distribution', label: 'Distribution' },
]
